export class MathHelper {
  /**
   * Determine the exponent of the highest power of two that is less than or equal to x.
   *
   * @param x The number to calculate the exponent for.
   * @param base The base to calculate the exponent for. Defaults to 2.
   *
   * @returns The calculated exponent.
   */
  public static exponentOfHighestPowerOfBase(x: number, base = 2): number {
    return Math.floor(Math.log(x) / Math.log(base));
  }

  /**
   * Convert a number from radians to degrees.
   *
   * @param radians The number in radians to convert to degrees.
   *
   * @returns The converted number in degrees.
   */
  public static radToDeg(radians: number): number {
    return radians * (180 / Math.PI);
  }

  /**
   * Convert a number from degrees to radians.
   *
   * @param degrees The number in degrees to convert to radians.
   *
   * @returns The converted number in radians.
   */
  public static degToRad(degrees: number): number {
    return degrees * (Math.PI / 180);
  }

  /**
   * Clamp a value between min and max.
   *
   * @param value The value to clamp.
   * @param min The minimum value.
   * @param max The maximum value.
   *
   * @returns The clamped value.
   */
  public static clamp(value: number, min: number, max: number): number {
    return Math.max(min, Math.min(value, max));
  }

  /**
   * Linear interpolation between two values.
   *
   * @param start The start value.
   * @param end The end value.
   * @param t The interpolation factor (0-1).
   *
   * @returns The interpolated value.
   */
  public static lerp(start: number, end: number, t: number): number {
    return start + (end - start) * t;
  }

  /**
   * Calculate the percentage of a value within a range.
   *
   * @param value The value to calculate percentage for.
   * @param min The minimum value of the range.
   * @param max The maximum value of the range.
   *
   * @returns The percentage (0-1).
   */
  public static percentage(value: number, min: number, max: number): number {
    if (max === min) {
      return 0;
    }
    return (value - min) / (max - min);
  }

  /**
   * Round a number to a specified number of decimal places.
   *
   * @param value The value to round.
   * @param decimals The number of decimal places.
   *
   * @returns The rounded value.
   */
  public static round(value: number, decimals = 0): number {
    const factor = Math.pow(10, decimals);

    return Math.round(value * factor) / factor;
  }

  /**
   * Calculate aspect ratio from dimensions.
   *
   * @param width - Width value.
   * @param height - Height value.
   * @returns Aspect ratio (width/height).
   */
  public static calculateAspectRatio(width: number, height: number): number {
    return width && height ? width / height : 1;
  }
}
